import React, { useState, Suspense } from 'react';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { PageNode, Component } from '@myriadgenetics/mgh-types';
import ContinueWrapper from '../continue-wrapper';
import { typeToComponent, typeToComponentPilot } from '../../../helpers/component-map';
import Validation from '../../../validation';
import { Text2 } from '../../../elements/typography';
import NodeFooter from '../../../elements/node-footer';
import { getUniqueDataId } from '../helpers';
import { areAllValid, autoAble, isAutoType, isContinueInline, triggerContinue, trackGtmData } from '../helpers';
import wand from './wand.png';

import Card from '../../rac/card';
import PageNodeThemeProvider from './theme/PageNodeThemeProvider';
import { useUrl } from '../../../helpers/url';
import { useLocation } from '../../../helpers/routing';
import { LocationState } from '../../survey-container/types';

const PdfViewer = React.lazy(() => import('../../pdf-viewer'));
interface Props {
  node: any | PageNode;
  responses: any;
  gotoNextNode: (nodeId?: number | null) => void;
  onResponseUpdated: (answerKey: string, val: any) => void;
  isResultsPage?: boolean;
  isClosePageButtonExist?: boolean;
  hasBackButton?: boolean;
  gtmDataTracking?: ({ answerKey }: { answerKey: string }) => void;
  isDemo?: boolean;
  isAuthPage?: boolean;
}

function PageNodeReact({
  node,
  responses,
  gotoNextNode,
  onResponseUpdated,
  isResultsPage,
  isClosePageButtonExist,
  gtmDataTracking,
  hasBackButton,
  isAuthPage,
  isDemo,
}: Props) {
  const { t } = useTranslation();
  const { urlType } = useUrl();
  const { continueKey, continueType, subtitle, footerKey, isColumns } = node?.data || {};
  const validationMap = Validation.createValidationMap(node?.components || [], responses);
  const allAreValid = areAllValid(validationMap);
  const [isPdfVisible, setIsPdfVisible] = useState<boolean>(false);
  const location = useLocation() as LocationState;
  const surveyAccount = location.state?.surveyAccount;
  const hasInOfficeTitle = node?.components?.some((c: Component) => c.type === 'TEXT_CONTENT' && c.data.contentKey === 'results.pilot.met.inOfficeTitle');
  const isOneMedicalResultsPage = node?.components?.some((c: Component) => c.type === 'TEXT_CONTENT' && c.data.contentKey?.includes('results.oneMed.'));

  const triggerOnResponseUpdated = (answerKey: string, val: any): void => {
    onResponseUpdated(answerKey, val);

    const validationMap = Validation.createValidationMap(node?.components || [], responses);
    const allAreValid = areAllValid(validationMap);

    isAutoType(continueType) && allAreValid && autoAble(node, get, responses) && triggerContinue(gotoNextNode, node, responses);
  };

  const uniqueDataId = node && `chat-modal-item--${getUniqueDataId(node.id, node.name)}`;

  return (
    <PageNodeThemeProvider>
      <div className={`page-node  ${isColumns ? 'page-node--columns' : ''} ${isOneMedicalResultsPage ? 'onemed' : ''} `} data-testid={uniqueDataId}>
        {subtitle && <Text2 className="page-node__subtitle subtitle">{t(subtitle)}</Text2>}
        <Card>
          <div className="page-node__content">
            {node &&
              node.components?.map((c: Component, i: number) => {
                const key = Validation.getKey(c, i);
                // TODO remove after close buttons are no longer inlined
                if (isResultsPage && (c.type === 'BUTTON' || c.type === 'CONTINUE')) {
                  return null;
                } else if (c.type === 'CONTINUE') {
                  return (
                    <ContinueWrapper
                      key={key}
                      allAreValid={allAreValid}
                      continueType={continueType}
                      isResultsPage={isResultsPage}
                      triggerContinue={() => triggerContinue(gotoNextNode, node, responses)}
                      isClosePageButtonExist={isClosePageButtonExist}
                      gtmDataTracking={() => {
                        trackGtmData(node, gtmDataTracking);
                      }}
                      hasBackButton={hasBackButton}
                      isAuthPage={isAuthPage}
                    />
                  );
                } else if (isResultsPage) {
                  const [Tag, className] = typeToComponentPilot(c);

                  // TODO remove after a QUALIFY_BANNER component is in admin tool
                  // No current way to tell if in office but is needed for showing inside banner
                  if (Tag === null) return null;

                  return (
                    <Tag
                      key={key}
                      surveyAccount={surveyAccount}
                      isInOffice={hasInOfficeTitle ? hasInOfficeTitle : undefined}
                      compDef={c}
                      className={className}
                      responses={responses}
                      onResponseUpdated={triggerOnResponseUpdated}
                      validationResult={validationMap[key]}
                      onClick={triggerContinue}
                      showContinueButton={!isAutoType(continueType)}
                      gtmDataTracking={() => {
                        trackGtmData(node, gtmDataTracking);
                      }}
                    />
                  );
                } else {
                  const Tag = typeToComponent(c);
                  return (
                    <Tag
                      surveyAccount={surveyAccount}
                      key={key}
                      compDef={c}
                      responses={responses}
                      onResponseUpdated={triggerOnResponseUpdated}
                      validationResult={validationMap[key]}
                      onClick={triggerContinue}
                      showContinueButton={!isAutoType(continueType)}
                      gtmDataTracking={() => {
                        trackGtmData(node, gtmDataTracking);
                      }}
                    />
                  );
                }
              })}
            {node && urlType !== 'classic' && !isContinueInline(node) && (
              <ContinueWrapper
                continueKey={continueKey}
                allAreValid={allAreValid}
                continueType={continueType}
                isResultsPage={isResultsPage}
                triggerContinue={() => triggerContinue(gotoNextNode, node, responses)}
                isClosePageButtonExist={isClosePageButtonExist}
                gtmDataTracking={() => {
                  trackGtmData(node, gtmDataTracking);
                }}
                hasBackButton={hasBackButton}
                isAuthPage={isAuthPage}
              />
            )}
          </div>
        </Card>
        {node && urlType === 'classic' && isResultsPage && (
          <ContinueWrapper
            continueKey={continueKey}
            allAreValid={allAreValid}
            continueType={continueType}
            isResultsPage={isResultsPage}
            triggerContinue={() => triggerContinue(gotoNextNode, node, responses)}
            isClosePageButtonExist={isClosePageButtonExist}
            gtmDataTracking={() => {
              trackGtmData(node, gtmDataTracking);
            }}
            hasBackButton={hasBackButton}
            isAuthPage={isAuthPage}
            isChatbotStyle={false}
          />
        )}
        {node && urlType === 'classic' && !isContinueInline(node) && !isResultsPage && (
          <ContinueWrapper
            continueKey={continueKey}
            allAreValid={allAreValid}
            continueType={continueType}
            isResultsPage={isResultsPage}
            triggerContinue={() => triggerContinue(gotoNextNode, node, responses)}
            isClosePageButtonExist={isClosePageButtonExist}
            gtmDataTracking={() => {
              trackGtmData(node, gtmDataTracking);
            }}
            hasBackButton={hasBackButton}
            isAuthPage={isAuthPage}
            isChatbotStyle={false}
          />
        )}
        {responses?.surveyEntrySave?.encodedPdf && isDemo && (
          <img data-testid="pdf-magic-wand" alt="magic" className="person-demographic__wand" src={wand} onClick={() => setIsPdfVisible(!isPdfVisible)} />
        )}
        {isPdfVisible && (
          <dialog open id="pdf-viewer-dialog" className="page-node__pdf-viewer-dialog">
            <Suspense fallback={<div>Loading...</div>}>
              <PdfViewer encodedPdf={responses?.surveyEntrySave?.encodedPdf}></PdfViewer>
            </Suspense>
          </dialog>
        )}
        {footerKey && <NodeFooter footerKey={footerKey} />}
      </div>
    </PageNodeThemeProvider>
  );
}
export default PageNodeReact;
